<template>
  <!-- Your five once their games are under way: live lanes, then the ones
       still to come, then the finished ones -- and, once all five are final,
       a strip of tiles that each open their cover chart. Before any of them
       kicks off the page keeps its review-and-send card instead. -->
  <section
    class="mb-4 rounded-2xl border border-white/10 bg-brand-navy/40 p-2.5 shadow-lg shadow-black/15 sm:p-3"
    aria-labelledby="gameday-picks-heading"
  >
    <header class="flex items-center justify-between gap-3 px-0.5">
      <h2 id="gameday-picks-heading" class="text-sm font-extrabold text-white">Your Picks</h2>
      <div class="text-right">
        <p class="text-lg font-black leading-tight tabular-nums text-white">{{ summary.record }}</p>
        <p class="text-[10px] font-bold text-slate-400">{{ headerNote }}</p>
      </div>
    </header>

    <!-- Week settled: five tiles; tapping one opens its detail below. -->
    <template v-if="summary.settled">
      <div class="mt-2.5 grid grid-cols-5 gap-1.5">
        <button
          v-for="pick in picks"
          :key="pick.id"
          type="button"
          class="flex flex-col items-center rounded-xl px-0.5 pb-2 pt-2 transition-colors"
          :class="tileClass(pick)"
          :aria-expanded="openId === pick.id"
          :aria-label="`${pick.abbr} ${pick.lineText}, ${signedText(pick.cushion)} against the line. ${openId === pick.id ? 'Hide' : 'Show'} details`"
          @click="toggle(pick.id)"
        >
          <TeamLogoChip :team="pick.team" :abbr="pick.abbr" size="xs" />
          <span class="mt-1 text-[11px] font-extrabold leading-tight text-white">{{ pick.abbr }}</span>
          <span class="text-[10px] font-bold leading-tight text-slate-400">{{ pick.lineText }}</span>
          <span class="mt-1 text-sm font-black leading-tight tabular-nums" :class="cushionClass(pick.cushion)">{{ signedText(pick.cushion) }}</span>
          <span class="text-[9px] font-bold leading-tight tabular-nums text-slate-400">{{ pick.score }}</span>
          <span class="text-[9px] font-bold leading-tight text-slate-500">Final</span>
        </button>
      </div>

      <div v-if="openPick" class="mt-2.5 rounded-xl border border-white/10 bg-white/[0.03] p-3">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="text-sm font-extrabold text-white">{{ openPick.abbr }} {{ openPick.lineText }} {{ openPick.home ? "vs" : "@" }} {{ openPick.opp }}</p>
            <p class="mt-0.5 text-[11px] font-bold text-slate-400">Final · {{ openPick.abbr }} {{ openPick.us }} · {{ openPick.opp }} {{ openPick.them }}</p>
          </div>
          <div class="shrink-0 text-right">
            <p class="text-[22px] font-black leading-none tracking-tight tabular-nums" :class="cushionClass(openPick.cushion)">{{ signedText(openPick.cushion) }}</p>
            <p class="mt-1 text-[9px] font-extrabold tracking-[0.12em] text-slate-400">VS THE LINE</p>
          </div>
        </div>
        <p class="mt-2 text-xs text-slate-200">{{ openPick.cushion > 0 ? "Covered" : "Missed" }} by {{ Math.abs(openPick.cushion) }}.</p>
        <CoverChart v-if="openPick.path" class="mt-2" :path="openPick.path" :tone="openPick.cushion > 0 ? 'win' : 'loss'" />
        <div class="mt-2 flex flex-wrap gap-1.5">
          <span v-if="openPick.pregame != null" class="chip">Pre-game {{ percent1(openPick.pregame) }}<template v-if="openPick.band"> · {{ openPick.band }}</template></span>
          <template v-if="openPick.range">
            <span class="chip">Lowest {{ chance(openPick.range.low) }}</span>
            <span class="chip">Highest {{ chance(openPick.range.high) }}</span>
          </template>
        </div>
      </div>
    </template>

    <!-- Games under way: live, then up next, then final. -->
    <template v-else>
      <template v-if="livePicks.length">
        <!-- Same three columns as each lane below, so SHORT / LINE / INSIDE sit
             over the lane's left end, the frozen-line tick and its right end at
             any width. -->
        <div class="mt-3 flex items-baseline gap-2 px-0.5">
          <span class="w-[84px] shrink-0 text-[9px] font-extrabold tracking-[0.18em] text-amber-400">LIVE</span>
          <span class="flex flex-1 justify-between text-[9px] font-extrabold tracking-[0.08em] text-slate-500"><span>SHORT</span><span>LINE</span><span>INSIDE</span></span>
          <span class="w-11 shrink-0"></span>
        </div>
        <div v-for="pick in livePicks" :key="pick.id" class="border-t border-white/[0.08] px-0.5 pb-2.5 pt-3">
          <!-- The whole lane is the tap target: it opens the pick's chance to
               cover through the game so far. -->
          <button
            type="button"
            class="flex w-full items-center gap-2 text-left"
            :aria-expanded="openId === pick.id"
            :aria-label="`${laneLabel(pick)}. ${openId === pick.id ? 'Hide' : 'Show'} chart`"
            @click="toggle(pick.id)"
          >
            <div class="flex w-[84px] shrink-0 items-center gap-1.5">
              <TeamLogoChip :team="pick.team" :abbr="pick.abbr" size="xs" />
              <div class="min-w-0">
                <p class="whitespace-nowrap text-xs font-extrabold text-white">{{ pick.abbr }} {{ pick.lineText }}</p>
                <p class="whitespace-nowrap text-[10px] font-bold text-amber-400">{{ pick.clock }}</p>
              </div>
            </div>
            <div class="relative h-[26px] flex-1" role="img" :aria-label="laneLabel(pick)">
              <span class="absolute left-0 right-1/2 top-[11px] h-1 rounded-l bg-rose-400/25"></span>
              <span class="absolute left-1/2 right-0 top-[11px] h-1 rounded-r bg-emerald-400/25"></span>
              <span class="absolute left-1/2 top-[3px] -ml-px h-5 w-0.5 bg-white/55"></span>
              <span
                class="absolute top-[6px] -ml-[7px] h-3.5 w-3.5 rounded-full border-2 border-[#071425]"
                :class="pick.live && pick.live.p >= 0.5 ? 'bg-emerald-400' : 'bg-rose-400'"
                :style="{ left: lanePosition(pick) }"
              ></span>
              <span class="absolute -top-2 -translate-x-1/2 whitespace-nowrap text-[9px] font-extrabold tabular-nums text-slate-200" :style="{ left: lanePosition(pick) }">{{ pick.score }}</span>
            </div>
            <p class="w-11 shrink-0 text-right text-lg font-black tracking-tight tabular-nums" :class="pick.live ? (pick.live.p >= 0.5 ? 'text-emerald-400' : 'text-rose-400') : 'text-slate-400'">
              {{ pick.live ? chance(pick.live.p) : "—" }}
            </p>
          </button>
          <div v-if="pick.live" class="mt-1.5 flex gap-2">
            <span class="w-[84px] shrink-0"></span>
            <p class="flex-1 whitespace-nowrap text-center text-[10px] font-semibold text-slate-400">
              {{ pick.abbr }} TD → {{ chance(pick.live.ifSideScores) }} · {{ pick.opp }} TD → {{ chance(pick.live.ifOpponentScores) }}
            </p>
            <span class="w-11 shrink-0"></span>
          </div>
          <CoverDetail
            v-if="openId === pick.id"
            class="mt-2.5"
            :path="pick.path"
            :now="pick.live ? pick.live.p : null"
            :pregame="pick.pregame"
            :band="pick.band"
          />
        </div>
      </template>

      <template v-if="upcomingPicks.length">
        <p class="mt-2 px-0.5 pb-1 text-[9px] font-extrabold tracking-[0.18em] text-slate-400">UP NEXT</p>
        <div
          v-for="pick in upcomingPicks"
          :key="pick.id"
          class="grid min-h-[40px] grid-cols-[14px_28px_minmax(0,1fr)_auto_46px] items-center gap-x-2 border-t border-white/[0.08] px-0.5"
        >
          <span class="number">{{ pick.n }}</span>
          <TeamLogoChip :team="pick.team" :abbr="pick.abbr" size="xs" />
          <span class="truncate text-xs font-extrabold text-white">{{ pick.abbr }} {{ pick.lineText }} <span class="text-[10px] font-semibold text-slate-500">{{ pick.home ? "v" : "@" }} {{ pick.opp }}</span></span>
          <span class="text-[11px] font-bold text-slate-400">{{ pick.kickoff }}</span>
          <span class="text-right text-[13px] font-extrabold tabular-nums text-slate-300">{{ pick.pregame == null ? "—" : percent1(pick.pregame) }}</span>
        </div>
      </template>

      <template v-if="finalPicks.length">
        <p class="mt-2 px-0.5 pb-1 text-[9px] font-extrabold tracking-[0.18em] text-slate-400">FINAL</p>
        <div
          v-for="pick in finalPicks"
          :key="pick.id"
          class="grid min-h-[40px] grid-cols-[14px_28px_minmax(0,1fr)_44px_44px_44px] items-center gap-x-1.5 border-t border-white/[0.08] px-0.5"
        >
          <span class="number">{{ pick.n }}</span>
          <TeamLogoChip :team="pick.team" :abbr="pick.abbr" size="xs" />
          <span class="truncate text-xs font-extrabold text-white">{{ pick.abbr }} {{ pick.lineText }}</span>
          <span class="text-center text-xs font-bold tabular-nums text-slate-300">{{ pick.score }}</span>
          <CoverChart v-if="pick.path" :path="pick.path" :tone="pick.cushion > 0 ? 'win' : 'loss'" spark />
          <span v-else></span>
          <span class="text-right text-xs font-extrabold tabular-nums" :class="cushionClass(pick.cushion)">{{ signedText(pick.cushion) }}</span>
        </div>
      </template>
    </template>
  </section>
</template>

<script>
import TeamLogoChip from "./TeamLogoChip.vue";
import CoverChart from "./CoverChart.vue";
import CoverDetail from "./CoverDetail.vue";
import { formatChance } from "@/utils/liveCover.js";

const LANE_SPAN = 14;

export default {
  name: "GamedayPicks",
  components: { TeamLogoChip, CoverChart, CoverDetail },
  props: {
    // `gamedayPicks` / `gamedaySummary` from utils/gameday.js.
    picks: { type: Array, required: true },
    summary: { type: Object, required: true },
  },
  data() {
    return { openId: null };
  },
  computed: {
    livePicks() {
      return this.picks.filter((pick) => pick.state === "live");
    },
    upcomingPicks() {
      return this.picks.filter((pick) => pick.state === "pre");
    },
    finalPicks() {
      return this.picks.filter((pick) => pick.state === "final");
    },
    openPick() {
      return this.picks.find((pick) => pick.id === this.openId) || null;
    },
    headerNote() {
      if (this.summary.settled) return `pre-game expected ${this.summary.expected.toFixed(1)}`;
      const parts = [];
      if (this.summary.live) parts.push(`${this.summary.live} live`);
      parts.push(`projected ${this.summary.projected.toFixed(1)}`);
      if (this.summary.sweep != null && this.summary.sweep > 0) parts.push(`5–0 ${formatChance(this.summary.sweep)}`);
      return parts.join(" · ");
    },
  },
  methods: {
    toggle(id) {
      this.openId = this.openId === id ? null : id;
    },
    chance(p) {
      return formatChance(p);
    },
    percent1(p) {
      return `${(p * 100).toFixed(1)}%`;
    },
    signedText(value) {
      if (value == null) return "—";
      if (value === 0) return "0";
      return `${value > 0 ? "+" : "−"}${Math.abs(value)}`;
    },
    cushionClass(value) {
      if (value == null || value === 0) return "text-slate-300";
      return value > 0 ? "text-emerald-400" : "text-rose-400";
    },
    tileClass(pick) {
      const won = pick.cushion > 0;
      const tone = won ? "border-emerald-400/35 bg-emerald-400/[0.07]" : "border-rose-400/35 bg-rose-400/[0.07]";
      return this.openId === pick.id ? `${tone} border-2 !border-slate-200` : `${tone} border`;
    },
    lanePosition(pick) {
      const cushion = pick.cushion ?? 0;
      const clamped = Math.max(-LANE_SPAN, Math.min(LANE_SPAN, cushion));
      return `${50 + (clamped / LANE_SPAN) * 50}%`;
    },
    laneLabel(pick) {
      if (pick.cushion == null) return `${pick.abbr} live`;
      const where = pick.cushion > 0 ? `${pick.cushion} inside` : `${Math.abs(pick.cushion)} short of`;
      return `${pick.abbr} ${where} the line${pick.live ? `, ${formatChance(pick.live.p)} to cover` : ""}`;
    },
  },
};
</script>

<style scoped>
.chip { border-radius: 9999px; background: rgb(255 255 255 / 0.06); padding: 0.25rem 0.5rem; color: #cbd5e1; font-size: 10px; font-weight: 700; white-space: nowrap; }
.number { display: flex; height: 14px; width: 14px; align-items: center; justify-content: center; border-radius: 9999px; border: 1.5px solid #34d399; color: #34d399; font-size: 8px; font-weight: 800; }
</style>
